import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Empty, Icon, Select, message } from "antd";
import { CompanyService, UnderwritingService } from "services/index";
import { fetchCaseLedgerMasters } from "services/case-service";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from "recharts";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import TriangulationChart from "./components/triangulation-chart";
import TriangulationTable from "./components/triangulation-table";
import TriangulationDiagram from "./components/triangulation-diagram";
import ReconciliationBar from "./components/reconciliation-bar";
import TriangulationChecks from "./components/triangulation-checks";
// BankingAnalysisCard removed — its KPI summary is already inside
// BankStatementAnalysisTab, so keeping both duplicated the metrics on screen.
import BankAccountCard from "./bank-account-card";
import BankStatementAnalysisTab from "../bank-statement-analysis-tab";
import CompanyDocViewerModal from "./components/company-doc-viewer-modal";
import { groupMonthly } from "./triangulation-group";
import {
    GROUP_OPTIONS,
    UNIT_OPTIONS,
    unitLabel,
    formatAmount,
    scaleFromLakhs,
    unitSuffix
} from "./triangulation-format";

// Period A / Period B accent colours (on-theme blue + teal).
const CMP_A_FILL = "#2563eb";
const CMP_B_FILL = "#14b8a6";
// The API returns up to 24 months; the monthly triangulation opens on the most
// recent 12. Older months stay selectable via the From picker.
const DEFAULT_MONTH_WINDOW = 12;
import { SECTION_IDS, TRIANGULATION_DOCUMENT_TILES } from "./constants";

const { Option } = Select;

// Ledger PDF download helper — copied from the Credit Appraisal ledger analysis
// (ledger-master-table.js) so the download behaves identically: PDF comes back
// as a raw arraybuffer written to a Blob with its content-type.
const downloadFileLocally = (res, fileType, type, name = "ledger") => {
    const blob = new Blob([res], { type });
    const elem = window.document.createElement("a");
    elem.href = window.URL.createObjectURL(blob);
    elem.download = `${name}.${fileType}`;
    document.body.appendChild(elem);
    elem.click();
    document.body.removeChild(elem);
};

function provenanceNote(provenance) {
    if (!provenance) {
        return null;
    }
    const real = Object.keys(provenance).filter(k => provenance[k] === "real");
    const mock = Object.keys(provenance).filter(k => provenance[k] === "mock");
    const label = key => key.toUpperCase();
    const parts = [];
    if (real.length) {
        parts.push(`Live: ${real.map(label).join(", ")}`);
    }
    if (mock.length) {
        parts.push(`Indicative: ${mock.map(label).join(", ")}`);
    }
    return parts.join(" · ");
}

/**
 * GST / Ledger / Banking triangulation. Order:
 *   1. Source correlation & analyst checks — the diagram, gating checks, and the
 *      underlying source documents (GST returns, ledger, bank statement).
 *   2. Monthly triangulation — chart / numbers view with a date filter.
 *   3. Auto-flags.
 */
class TriangulationSection extends React.Component {
    state = {
        chartView: "numbers",
        fromIdx: null,
        toIdx: null,
        docOpen: false,
        docTab: null,
        groupBy: "month",
        unit: "lacs",
        // Period comparison view — two independent month ranges (A vs B). Null
        // indices fall back to sensible defaults (first ~3 months vs last ~3).
        cmpFromA: null,
        cmpToA: null,
        cmpFromB: null,
        cmpToB: null,
        // Ledger presence + supply-chain id come off the case ledger-masters
        // list (GET /case/:id/ledger-masters/) — the SAME source Credit
        // Appraisal uses — rather than looking for an uploaded "ledger" document.
        ledgerPresent: false,
        ledgerSupplychainId: null,
        ledgerId: null
    };

    componentDidMount() {
        this.loadLedgerMasters();
    }

    // Fetch the ledger-masters once: a ledger is "on file" when a supply chain
    // has an analysed ledger (ledger_analysis.id); the matching supplychain_id
    // is what the download uses.
    loadLedgerMasters = () => {
        if (!this.props.caseId) {
            return;
        }
        fetchCaseLedgerMasters(this.props.caseId)
            .then(res => {
                const rows = (res && res.data) || [];
                const analyzed = rows.filter(
                    r => r && r.ledger_analysis && r.ledger_analysis.id
                );
                const chosen = analyzed[0] || rows[0] || null;
                this.setState({
                    ledgerPresent: analyzed.length > 0,
                    ledgerSupplychainId:
                        (chosen &&
                            (chosen.supplychain_id ||
                                chosen.supply_chain_id)) ||
                        null,
                    ledgerId: (chosen && chosen.id) || null
                });
            })
            .catch(() => {});
    };

    setView = view => this.setState({ chartView: view });

    setGroupBy = groupBy => this.setState({ groupBy });

    setUnit = unit => this.setState({ unit });

    setFrom = idx =>
        this.setState(prev => ({
            fromIdx: idx,
            toIdx: prev.toIdx !== null && prev.toIdx < idx ? idx : prev.toIdx
        }));

    // `currentFrom` is the *effective* From shown in the picker — either the
    // user's pick or the default window start — so picking a To before it drags
    // From back with it instead of leaving an inverted (empty) range.
    setTo = (idx, currentFrom) =>
        this.setState(prev => {
            const from = prev.fromIdx === null ? currentFrom : prev.fromIdx;
            return { toIdx: idx, fromIdx: idx < from ? idx : prev.fromIdx };
        });

    resetRange = () => this.setState({ fromIdx: null, toIdx: null });

    // ── Period comparison (client-side; reuses the already-loaded monthly data) ──
    setCmp = (key, idx) => this.setState({ [key]: idx });

    // Sum GST + Ledger Sales across a month range (order-agnostic), plus the
    // GST↔Ledger match % for the aggregated totals and a "Mon – Mon" label.
    periodTotals = (months, fromIdx, toIdx) => {
        const lo = Math.min(fromIdx, toIdx);
        const hi = Math.max(fromIdx, toIdx);
        const slice = months.slice(lo, hi + 1);
        const gst = slice.reduce((a, r) => a + (Number(r.gst) || 0), 0);
        const ledger = slice.reduce((a, r) => a + (Number(r.ledger) || 0), 0);
        const denom = Math.max(gst, ledger);
        const match = denom
            ? Math.round((Math.min(gst, ledger) / denom) * 1000) / 10
            : null;
        const label = slice.length
            ? `${months[lo].month} – ${months[hi].month}`
            : "—";
        return { gst, ledger, match, label };
    };

    // Period-B-over-A growth %, or null when A is 0/absent.
    growthPct = (a, b) => {
        if (!a || Number.isNaN(Number(a))) {
            return null;
        }
        return Math.round(((b - a) / a) * 1000) / 10;
    };

    // Δ% as a filled pill (tinted green / red / grey) — used on the metric cards.
    growthPill(a, b) {
        const g = this.growthPct(a, b);
        if (g === null) {
            return (
                <span className={"cc-tri-cmp__pill cc-tri-cmp__pill--flat"}>
                    —
                </span>
            );
        }
        const dir = g > 0 ? "up" : g < 0 ? "down" : "flat";
        const arrow = dir === "up" ? "▲" : dir === "down" ? "▼" : "•";
        return (
            <span className={"cc-tri-cmp__pill cc-tri-cmp__pill--" + dir}>
                {arrow} {Math.abs(g)}%
            </span>
        );
    }

    // One period's From→To month pickers.
    periodPicker(label, months, fromKey, fromIdx, toKey, toIdx) {
        return (
            <div className={"cc-tri-cmp__pick"}>
                <span className={"cc-tri-cmp__plabel"}>{label}</span>
                <Select
                    size={"small"}
                    value={fromIdx}
                    onChange={idx => this.setCmp(fromKey, idx)}
                    dropdownMatchSelectWidth={false}
                    style={{ minWidth: 92 }}
                >
                    {months.map((m, i) => (
                        <Option key={m.month} value={i}>
                            {m.month}
                        </Option>
                    ))}
                </Select>
                <span className={"cc-tri-filter__label"}>to</span>
                <Select
                    size={"small"}
                    value={toIdx}
                    onChange={idx => this.setCmp(toKey, idx)}
                    dropdownMatchSelectWidth={false}
                    style={{ minWidth: 92 }}
                >
                    {months.map((m, i) => (
                        <Option key={m.month} value={i}>
                            {m.month}
                        </Option>
                    ))}
                </Select>
            </div>
        );
    }

    // Rendered inline as the "Comparison" view of the monthly-triangulation
    // card, alongside Chart / Numbers.
    renderComparePanel(months) {
        const { unit } = this.state;
        const last = months.length - 1;
        // Default: Period A = first up-to-3 months, Period B = last up-to-3.
        const aFrom = this.state.cmpFromA === null ? 0 : this.state.cmpFromA;
        const aTo =
            this.state.cmpToA === null ? Math.min(2, last) : this.state.cmpToA;
        const bFrom =
            this.state.cmpFromB === null
                ? Math.max(0, last - 2)
                : this.state.cmpFromB;
        const bTo = this.state.cmpToB === null ? last : this.state.cmpToB;
        const A = this.periodTotals(months, aFrom, aTo);
        const B = this.periodTotals(months, bFrom, bTo);
        const matchDelta =
            A.match !== null && B.match !== null
                ? Math.round((B.match - A.match) * 10) / 10
                : null;
        const suffix = unitSuffix(unit);
        const fmtChart = v =>
            "₹" +
            Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }) +
            suffix;
        const chartData = [
            {
                metric: "GST",
                a: scaleFromLakhs(A.gst, unit),
                b: scaleFromLakhs(B.gst, unit)
            },
            {
                metric: "Ledger Sales",
                a: scaleFromLakhs(A.ledger, unit),
                b: scaleFromLakhs(B.ledger, unit)
            }
        ];
        const metricCard = (label, aVal, bVal) => (
            <div className={"cc-tri-cmp__card"}>
                <div className={"cc-tri-cmp__card-top"}>
                    <span className={"cc-tri-cmp__card-hd"}>{label}</span>
                    {this.growthPill(aVal, bVal)}
                </div>
                <div className={"cc-tri-cmp__card-row"}>
                    <span
                        className={
                            "cc-tri-cmp__tag cc-tri-cmp__tag--a"
                        }
                    >
                        A
                    </span>
                    <span className={"cc-tri-cmp__card-val"}>
                        {formatAmount(aVal, unit) || "—"}
                    </span>
                </div>
                <div className={"cc-tri-cmp__card-row"}>
                    <span
                        className={
                            "cc-tri-cmp__tag cc-tri-cmp__tag--b"
                        }
                    >
                        B
                    </span>
                    <span className={"cc-tri-cmp__card-val"}>
                        {formatAmount(bVal, unit) || "—"}
                    </span>
                </div>
            </div>
        );
        return (
            <div className={"cc-tri-cmp-panel"}>
                <div className={"cc-tri-cmp__title"}>
                    Compare Periods — GST vs Ledger Sales
                </div>
                {last < 0 ? (
                    <Empty description={"No monthly data to compare"} />
                ) : (
                    <div className={"cc-tri-cmp"}>
                        <div className={"cc-tri-cmp__pickers"}>
                            {this.periodPicker(
                                "Period A",
                                months,
                                "cmpFromA",
                                aFrom,
                                "cmpToA",
                                aTo
                            )}
                            {this.periodPicker(
                                "Period B",
                                months,
                                "cmpFromB",
                                bFrom,
                                "cmpToB",
                                bTo
                            )}
                        </div>
                        <div className={"cc-tri-cmp__cards"}>
                            {metricCard("GST", A.gst, B.gst)}
                            {metricCard("Ledger Sales", A.ledger, B.ledger)}
                        </div>

                        <div className={"cc-tri-cmp__legend"}>
                            <span>
                                <span
                                    className={
                                        "cc-tri-cmp__dot cc-tri-cmp__dot--a"
                                    }
                                />
                                A · {A.label}
                            </span>
                            <span>
                                <span
                                    className={
                                        "cc-tri-cmp__dot cc-tri-cmp__dot--b"
                                    }
                                />
                                B · {B.label}
                            </span>
                        </div>
                        <div className={"cc-tri-cmp__chart"}>
                            <ResponsiveContainer width={"100%"} height={240}>
                                <BarChart
                                    data={chartData}
                                    margin={{
                                        top: 8,
                                        right: 8,
                                        left: 0,
                                        bottom: 0
                                    }}
                                    barGap={6}
                                    barCategoryGap={"32%"}
                                >
                                    <CartesianGrid
                                        vertical={false}
                                        stroke={"#eef1f5"}
                                    />
                                    <XAxis
                                        dataKey={"metric"}
                                        tickLine={false}
                                        axisLine={{ stroke: "#e6e9ee" }}
                                        tick={{ fontSize: 12, fill: "#8a93a3" }}
                                    />
                                    <YAxis
                                        tickLine={false}
                                        axisLine={false}
                                        tick={{ fontSize: 12, fill: "#8a93a3" }}
                                        width={44}
                                    />
                                    <Tooltip
                                        cursor={{
                                            fill: "rgba(37,99,235,0.06)"
                                        }}
                                        formatter={v => [fmtChart(v)]}
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: "1px solid #e6e9ee",
                                            fontSize: 12
                                        }}
                                    />
                                    <Legend
                                        iconType={"circle"}
                                        wrapperStyle={{
                                            fontSize: 12,
                                            paddingTop: 4
                                        }}
                                    />
                                    <Bar
                                        dataKey={"a"}
                                        name={`A · ${A.label}`}
                                        fill={CMP_A_FILL}
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={54}
                                    />
                                    <Bar
                                        dataKey={"b"}
                                        name={`B · ${B.label}`}
                                        fill={CMP_B_FILL}
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={54}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className={"cc-tri-cmp__match"}>
                            <span className={"cc-tri-cmp__match-lbl"}>
                                GST ↔ Ledger match
                            </span>
                            <span>
                                A {A.match === null ? "—" : `${A.match}%`}
                            </span>
                            <span>
                                B {B.match === null ? "—" : `${B.match}%`}
                            </span>
                            {matchDelta !== null ? (
                                <span
                                    className={
                                        "cc-tri-mom cc-tri-mom--" +
                                        (matchDelta > 0
                                            ? "up"
                                            : matchDelta < 0
                                                ? "down"
                                                : "flat")
                                    }
                                >
                                    {matchDelta > 0 ? "+" : ""}
                                    {matchDelta} pt
                                </span>
                            ) : null}
                        </div>
                        <div className={"cc-provenance"}>
                            Totals are the sum of monthly {unitLabel(unit)} in each
                            selected range · Δ% is Period B over Period A.
                        </div>
                    </div>
                )}
            </div>
        );
    }

    openDoc = key => this.setState({ docOpen: true, docTab: key });

    closeDoc = () => this.setState({ docOpen: false });

    // Latest GST-pull download — same source the Credit Appraisal tab's "GST
    // Pull Data" tile uses (`CompanyService.getGstData(gstin)` → newest
    // `doc_url`). Distinct from the uploaded "GST Returns" document (id 97),
    // which is what the eye icon opens.
    downloadGstPull = () => {
        const gstin =
            this.props.company && this.props.company.gstin
                ? this.props.company.gstin
                : null;
        if (!gstin) {
            message.error("Company GSTIN not on file");
            return;
        }
        const loading = message.loading("Fetching GST pull…", 0);
        CompanyService.getGstData(gstin)
            .then(res => {
                loading();
                const latest = (res && res.data ? res.data : [])
                    .filter(el => el && el.doc_url)
                    .sort(
                        (a, b) =>
                            new Date(b.create_date) - new Date(a.create_date)
                    )[0];
                if (latest && latest.doc_url) {
                    window.open(latest.doc_url, "_blank");
                } else {
                    message.error("GST pull data not available");
                }
            })
            .catch(() => {
                loading();
                message.error("Failed to fetch GST pull");
            });
    };

    // Ledger download — PDF only in Credit Checker. Replicates the Credit
    // Appraisal ledger "Download" (getLedgerFile(id, false, false, "pdf")): the
    // response is a raw arraybuffer whose file type is read off the
    // content-type header.
    // Pull the ledger PDF for a resolved supply-chain id (raw arraybuffer, file
    // type off the content-type header — same as Credit Appraisal).
    // Filename: case + ledger-master id so downloaded ledgers are traceable to
    // the case (e.g. "ledger-case-161313-ledger-482.pdf").
    ledgerFileName = ledgerId => {
        const parts = ["ledger"];
        if (this.props.caseId) {
            parts.push(`case-${this.props.caseId}`);
        }
        if (ledgerId) {
            parts.push(`ledger-${ledgerId}`);
        }
        return parts.join("-");
    };

    fetchLedgerPdf = (supplychainId, loading, ledgerId) => {
        if (!supplychainId) {
            loading();
            message.error("Supply chain not on case yet");
            return;
        }
        const name = this.ledgerFileName(ledgerId);
        UnderwritingService.getLedgerFile(supplychainId, false, false, "pdf")
            .then(fileRes => {
                loading();
                const contentType =
                    fileRes && fileRes.headers && fileRes.headers["content-type"];
                if (fileRes && fileRes.data && contentType === "application/pdf") {
                    downloadFileLocally(
                        fileRes.data,
                        "pdf",
                        "application/pdf",
                        name
                    );
                } else if (
                    fileRes &&
                    fileRes.data &&
                    contentType === "application/zip"
                ) {
                    downloadFileLocally(
                        fileRes.data,
                        "zip",
                        "application/zip",
                        name
                    );
                } else {
                    message.error("Ledger not available for this case yet");
                }
            })
            .catch(() => {
                loading();
                message.error("Failed to download ledger");
            });
    };

    downloadLedger = () => {
        const loading = message.loading(
            "Ledger download in progress, may take a few seconds…",
            0
        );
        // Prefer the supplychain_id resolved on mount; otherwise resolve it now
        // from the case ledger-masters list.
        if (this.state.ledgerSupplychainId) {
            this.fetchLedgerPdf(
                this.state.ledgerSupplychainId,
                loading,
                this.state.ledgerId
            );
            return;
        }
        fetchCaseLedgerMasters(this.props.caseId)
            .then(res => {
                const rows = (res && res.data) || [];
                const chosen =
                    rows.find(
                        r => r.ledger_analysis && r.ledger_analysis.id
                    ) || rows[0];
                this.fetchLedgerPdf(
                    chosen &&
                        (chosen.supplychain_id || chosen.supply_chain_id),
                    loading,
                    chosen && chosen.id
                );
            })
            .catch(() => {
                loading();
                message.error("Failed to download ledger");
            });
    };

    // Shown when there's no cross-source triangulation to plot (no overlapping
    // months). The reconciliation view can't be built, but the underlying
    // source documents (GST returns, ledger, bank statement) are still on file
    // and viewable/downloadable — so surface just the Source documents block
    // instead of collapsing the whole section to "not available".
    renderSourceDocsFallback(docs) {
        return (
            <CardBlock
                icon={"file-search"}
                title={"Source documents"}
                subtitle={
                    "Cross-source reconciliation isn't available (no overlapping months), but the underlying documents are on file."
                }
            >
                <div className={"cc-tri-docs-block"}>
                    {this.renderDocChips(docs)}
                </div>
            </CardBlock>
        );
    }

    renderDocChips(docs) {
        return (
            <div className={"cc-company__chips cc-tri-docs"}>
                {TRIANGULATION_DOCUMENT_TILES.map(tile => {
                    // Ledger isn't an uploaded document — its presence comes off
                    // the ledger-masters list (analysed ledger available), not a
                    // doc-name match.
                    const present =
                        tile.key === "ledger"
                            ? this.state.ledgerPresent
                            : Boolean(docs[tile.key]);
                    // GST tile carries a secondary "Download live GST pull"
                    // action; Ledger tile carries a "generate & download"
                    // Popconfirm (excel or pdf) that works even when nothing
                    // is on file.
                    const hasDownload =
                        tile.key === "gst" || tile.key === "ledger";
                    return (
                        <div
                            key={tile.key}
                            className={
                                "cc-company__chip cc-tri-doc" +
                                (present ? "" : " cc-company__chip--missing")
                            }
                            title={
                                present
                                    ? `${tile.label} — click eye to view`
                                    : `${tile.label} not on file`
                            }
                        >
                            <span className={"cc-company__chip-icon"}>
                                <Icon type={tile.icon} />
                            </span>
                            <span className={"cc-company__chip-body"}>
                                <span className={"cc-company__chip-label"}>
                                    {tile.label}
                                </span>
                                <span className={"cc-company__chip-state"}>
                                    {present ? "On file" : "Not on file"}
                                </span>
                            </span>
                            <span className={"cc-tri-doc__actions"}>
                                {/* GST tile: BOTH eye and download hit the
                                    live GST-aggregator pull (same source as
                                    Credit Appraisal's "GST Pull Data" card),
                                    NOT the uploaded doc-id-97 file — that
                                    upload is typically stale/wrong per credit
                                    team. Other tiles keep the doc-viewer
                                    eye that opens the uploaded file. */}
                                {tile.key === "gst" ? (
                                    <button
                                        type={"button"}
                                        className={"cc-tri-doc__btn"}
                                        onClick={this.downloadGstPull}
                                        title={
                                            "View latest GST pull (same as Credit Appraisal)"
                                        }
                                    >
                                        <Icon type={"eye"} />
                                    </button>
                                ) : present ? (
                                    <button
                                        type={"button"}
                                        className={"cc-tri-doc__btn"}
                                        onClick={() => this.openDoc(tile.key)}
                                        title={`View ${tile.label}`}
                                    >
                                        <Icon type={"eye"} />
                                    </button>
                                ) : null}
                                {tile.key === "gst" && hasDownload ? (
                                    <button
                                        type={"button"}
                                        className={"cc-tri-doc__btn"}
                                        onClick={this.downloadGstPull}
                                        title={
                                            "Download latest GST pull (same as Credit Appraisal)"
                                        }
                                    >
                                        <Icon type={"download"} />
                                    </button>
                                ) : null}
                                {tile.key === "ledger" && hasDownload ? (
                                    <button
                                        type={"button"}
                                        className={"cc-tri-doc__btn"}
                                        onClick={this.downloadLedger}
                                        title={
                                            "Download ledger (PDF) — same as Credit Appraisal"
                                        }
                                    >
                                        <Icon type={"download"} />
                                    </button>
                                ) : null}
                                {!present && !hasDownload ? (
                                    <Icon
                                        type={"warning"}
                                        className={"cc-company__chip-warn"}
                                    />
                                ) : null}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const {
            innerRef,
            triangulation,
            triangulationDocs,
            expanded,
            onToggle
        } = this.props;
        const { chartView, groupBy, unit } = this.state;
        // Manual/Analyser toggle, Rebuild Analysis, Upload Bank Statements
        // and the Bank Accounts actions (Change Key Account, Add Account,
        // Applicable To Case) are only actionable at the credit stages —
        // same gate used elsewhere in Credit Checker (TVR reset).
        const canEditAtCreditStage = [7, 8].includes(
            Number(this.props.stageId)
        );
        const docs = triangulationDocs || {};
        // Exclude the current (still-incomplete) month from the series so it
        // never appears in the From/To filter or the triangulation data.
        const currentMonthLabel = moment().format("MMM[']YY");
        const months = ((triangulation && triangulation.monthly) || []).filter(
            m => m && m.month !== currentMonthLabel
        );
        const lastIdx = months.length - 1;
        // Default range = the most recent DEFAULT_MONTH_WINDOW months.
        const defaultFromIdx = Math.max(0, lastIdx - (DEFAULT_MONTH_WINDOW - 1));
        const fromIdx =
            this.state.fromIdx === null ? defaultFromIdx : this.state.fromIdx;
        const toIdx = this.state.toIdx === null ? lastIdx : this.state.toIdx;
        const filteredMonthly = months.slice(fromIdx, toIdx + 1);
        const isFiltered = fromIdx !== defaultFromIdx || toIdx !== lastIdx;
        // Aggregate to the chosen grouping (month / quarter / FY / calendar year).
        const groupedData = groupMonthly(filteredMonthly, groupBy);
        // The Comparison view carries its own A/B month pickers, so the shared
        // range + group-by controls do not apply to it (only Unit does).
        const isCompare = chartView === "compare";

        return (
            <SectionBlock
                id={SECTION_IDS.TRIANGULATION}
                innerRef={innerRef}
                title={"GST / Ledger / Banking Triangulation"}
                subtitle={"Cross-source reconciliation and gating checks"}
                icon={"fund"}
                expanded={expanded}
                onToggle={onToggle}
            >
                {triangulation ? (
                    <React.Fragment>
                        <CardBlock
                            icon={"deployment-unit"}
                            title={"Source correlation & analyst checks"}
                            subtitle={
                                "GST↔Ledger (sales) · BSA↔Ledger (repayment) · ARP gating"
                            }
                        >
                            <div className={"cc-tri-correlate"}>
                                <TriangulationDiagram
                                    correlations={triangulation.correlations}
                                />
                                <div className={"cc-tri-correlate__checks"}>
                                    <TriangulationChecks
                                        checks={triangulation.checks}
                                    />
                                </div>
                            </div>
                            <div className={"cc-tri-docs-block"}>
                                <div className={"cc-tri-docs-block__title"}>
                                    Source documents
                                </div>
                                {this.renderDocChips(docs)}
                            </div>
                        </CardBlock>

                        <CardBlock
                            icon={"bar-chart"}
                            title={"Monthly triangulation"}
                            subtitle={`GST returns vs Tally ledger vs bank credits (${unitLabel(
                                unit
                            )})`}
                            action={
                                <span className={"cc-tri-actions"}>
                                    <div className={"cc-viewtoggle"}>
                                        <button
                                            type={"button"}
                                            className={
                                                "cc-viewtoggle__btn" +
                                                (chartView === "chart"
                                                    ? " cc-viewtoggle__btn--on"
                                                    : "")
                                            }
                                            onClick={() => this.setView("chart")}
                                        >
                                            <Icon type={"bar-chart"} /> Chart
                                        </button>
                                        <button
                                            type={"button"}
                                            className={
                                                "cc-viewtoggle__btn" +
                                                (chartView === "numbers"
                                                    ? " cc-viewtoggle__btn--on"
                                                    : "")
                                            }
                                            onClick={() =>
                                                this.setView("numbers")
                                            }
                                        >
                                            <Icon type={"table"} /> Numbers
                                        </button>
                                        <button
                                            type={"button"}
                                            className={
                                                "cc-viewtoggle__btn" +
                                                (chartView === "compare"
                                                    ? " cc-viewtoggle__btn--on"
                                                    : "")
                                            }
                                            onClick={() =>
                                                this.setView("compare")
                                            }
                                        >
                                            <Icon type={"swap"} /> Comparison
                                        </button>
                                    </div>
                                </span>
                            }
                        >
                            <div className={"cc-tri-filter"}>
                                {isCompare ? null : (
                                    <React.Fragment>
                                        <Icon
                                            type={"calendar"}
                                            className={"cc-tri-filter__icon"}
                                        />
                                        <span className={"cc-tri-filter__label"}>
                                            From
                                        </span>
                                        <Select
                                            size={"small"}
                                            value={fromIdx}
                                            onChange={this.setFrom}
                                            className={"cc-tri-filter__select"}
                                            dropdownMatchSelectWidth={false}
                                        >
                                            {months.map((m, i) => (
                                                <Option key={m.month} value={i}>
                                                    {m.month}
                                                </Option>
                                            ))}
                                        </Select>
                                        <span className={"cc-tri-filter__label"}>
                                            to
                                        </span>
                                        <Select
                                            size={"small"}
                                            value={toIdx}
                                            onChange={idx =>
                                                this.setTo(idx, fromIdx)
                                            }
                                            className={"cc-tri-filter__select"}
                                            dropdownMatchSelectWidth={false}
                                        >
                                            {months.map((m, i) => (
                                                <Option key={m.month} value={i}>
                                                    {m.month}
                                                </Option>
                                            ))}
                                        </Select>
                                        <span className={"cc-tri-filter__count"}>
                                            {filteredMonthly.length} of {months.length}{" "}
                                            months
                                        </span>
                                        <span className={"cc-tri-filter__sep"} />
                                        <span className={"cc-tri-filter__label"}>
                                            Group by
                                        </span>
                                        <Select
                                            size={"small"}
                                            value={groupBy}
                                            onChange={this.setGroupBy}
                                            className={"cc-tri-filter__select-wide"}
                                            dropdownMatchSelectWidth={false}
                                        >
                                            {GROUP_OPTIONS.map(o => (
                                                <Option key={o.key} value={o.key}>
                                                    {o.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </React.Fragment>
                                )}
                                <span className={"cc-tri-filter__label"}>
                                    Unit
                                </span>
                                <Select
                                    size={"small"}
                                    value={unit}
                                    onChange={this.setUnit}
                                    className={"cc-tri-filter__select"}
                                    dropdownMatchSelectWidth={false}
                                >
                                    {UNIT_OPTIONS.map(o => (
                                        <Option key={o.key} value={o.key}>
                                            {o.label}
                                        </Option>
                                    ))}
                                </Select>
                                {!isCompare && isFiltered ? (
                                    <button
                                        type={"button"}
                                        className={"cc-tri-filter__reset"}
                                        onClick={this.resetRange}
                                    >
                                        <Icon type={"close-circle"} /> Reset
                                    </button>
                                ) : null}
                            </div>
                            {isCompare ? (
                                this.renderComparePanel(months)
                            ) : chartView === "chart" ? (
                                <TriangulationChart
                                    data={groupedData}
                                    unit={unit}
                                />
                            ) : (
                                <TriangulationTable
                                    data={groupedData}
                                    unit={unit}
                                />
                            )}
                            <div className={"cc-recon-row"}>
                                {triangulation.reconciliations.map(rec => (
                                    <ReconciliationBar
                                        key={rec.key}
                                        label={rec.label}
                                        pct={rec.pct}
                                        band={rec.band}
                                        caption={rec.caption}
                                    />
                                ))}
                            </div>
                            {provenanceNote(triangulation.provenance) ? (
                                <div className={"cc-provenance"}>
                                    {provenanceNote(triangulation.provenance)}
                                </div>
                            ) : null}
                        </CardBlock>
                    </React.Fragment>
                ) : (
                    this.renderSourceDocsFallback(docs)
                )}

                {/* Doc viewer is shared by both branches (the fallback also
                    opens source documents), so it lives outside the ternary. */}
                <CompanyDocViewerModal
                    visible={this.state.docOpen}
                    docs={docs}
                    tiles={TRIANGULATION_DOCUMENT_TILES}
                    title={"Source documents"}
                    initialTab={this.state.docTab}
                    onClose={this.closeDoc}
                    onDownloadLedger={this.downloadLedger}
                />
                {/* Bank Statement Analysis — the SAME component the Credit
                    Appraisal tab renders, with the same feature set: the KPI
                    summary, the Jobs list (select / download / redo / rebuild /
                    upload) and the Manual-vs-Analyser switch.

                    `isSummary` is deliberately NOT passed: it strips the jobs
                    list and every header action, which is what used to leave
                    Credit Checker read-only here. `hideTitle` suppresses only the
                    inner heading, since CardBlock already provides one. The inner
                    antd cards are flattened by `.cc-bsa` so this reads as one
                    Credit-Checker surface rather than cards inside a card. */}
                <CardBlock
                    icon={"bank"}
                    title={"Bank Statement Analysis"}
                    className={"cc-bsa"}
                >
                    <BankStatementAnalysisTab
                        caseId={Number(this.props.caseId)}
                        companyId={this.props.companyId}
                        stageId={this.props.stageId}
                        lendingPartner={this.props.lendingPartner}
                        caseDetail={this.props.caseDetail}
                        accounts={
                            this.props.company &&
                            this.props.company.get &&
                            this.props.company.get("bank_accounts")
                        }
                        statements={this.props.statements}
                        fetchStatement={this.props.fetchStatement}
                        createStatement={this.props.createStatement}
                        updateStatement={this.props.updateStatement}
                        deleteStatement={this.props.deleteStatement}
                        updateCaseDetail={this.props.updateCaseDetail}
                        updateCaseDetailFields={this.props.updateCaseDetailFields}
                        use_statement_analyzer={this.props.use_statement_analyzer}
                        bankStatementAnalyser={this.props.bankStatementAnalyser}
                        createRequest={this.props.createRequest}
                        createProfiosRequest={this.props.createProfiosRequest}
                        fetchJobsList={this.props.fetchJobsList}
                        fetchJobsDetails={this.props.fetchJobsDetails}
                        fetchJobsDetailsPerfios={
                            this.props.fetchJobsDetailsPerfios
                        }
                        fetchStatementAnalysis={
                            this.props.fetchStatementAnalysis
                        }
                        fetchStatementAnalysisPerfois={
                            this.props.fetchStatementAnalysisPerfois
                        }
                        retryAnalysis={this.props.retryAnalysis}
                        retryAnalysisPerfios={this.props.retryAnalysisPerfios}
                        adminGroup={this.props.adminGroup}
                        userGroup={this.props.userGroup}
                        bankCode={this.props.bankCode}
                        isRiskApprover={this.props.isRiskApprover}
                        type={"inner"}
                        /* CardBlock above already supplies the heading. */
                        hideTitle
                        /* Credit-team can trigger Upload / Rebuild / Manual
                           Bank Statement Analysis from Credit Checker just
                           like they do from Credit Appraisal — no view-only
                           lock here. */
                        recreditViewOnlyPermission={!canEditAtCreditStage}
                        isUserAllowedToEdit
                    />
                </CardBlock>
                <BankAccountCard
                    caseId={this.props.caseId}
                    companyId={this.props.companyId}
                    company={this.props.company}
                    caseBankAccounts={this.props.caseBankAccounts}
                    caseBankAccountsStatus={this.props.caseBankAccountsStatus}
                    fetchCaseBankAccounts={this.props.fetchCaseBankAccounts}
                    addCaseBankAccounts={this.props.addCaseBankAccounts}
                    updateCompanyBankAccounts={
                        this.props.updateCompanyBankAccounts
                    }
                    isUserAllowedToEdit
                    recreditViewOnlyPermission={!canEditAtCreditStage}
                />
            </SectionBlock>
        );
    }
}

TriangulationSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Case stage — forwarded to the embedded Bank Statement Analyser so per-row
    // Credit Limit editing is enabled only at stages 7 and 8.
    stageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bankCode: PropTypes.string,
    company: PropTypes.object,
    triangulation: PropTypes.object,
    triangulationDocs: PropTypes.object,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    statements: PropTypes.object,
    fetchStatement: PropTypes.func,
    createStatement: PropTypes.func,
    updateStatement: PropTypes.func,
    deleteStatement: PropTypes.func,
    updateCaseDetail: PropTypes.func,
    updateCaseDetailFields: PropTypes.func,
    use_statement_analyzer: PropTypes.bool,
    bankStatementAnalyser: PropTypes.object,
    createRequest: PropTypes.func,
    createProfiosRequest: PropTypes.func,
    fetchJobsList: PropTypes.func,
    fetchJobsDetails: PropTypes.func,
    fetchJobsDetailsPerfios: PropTypes.func,
    fetchStatementAnalysis: PropTypes.func,
    fetchStatementAnalysisPerfois: PropTypes.func,
    retryAnalysis: PropTypes.func,
    retryAnalysisPerfios: PropTypes.func,
    adminGroup: PropTypes.object,
    userGroup: PropTypes.array,
    lendingPartner: PropTypes.number,
    caseDetail: PropTypes.object,
    isRiskApprover: PropTypes.bool,
    // Bank Accounts card (add / edit / key-account / applicability).
    caseBankAccounts: PropTypes.array,
    caseBankAccountsStatus: PropTypes.string,
    fetchCaseBankAccounts: PropTypes.func,
    addCaseBankAccounts: PropTypes.func,
    updateCompanyBankAccounts: PropTypes.func
};

export default TriangulationSection;
